import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Lock, Plus } from "lucide-react";
import { fr, chf } from "../content/fr";
import { fetchConfig, startCheckout } from "../lib/api";
import { track } from "../lib/tracking";
import Seo from "../components/Seo";
import Header from "../components/Header";
import Footer from "../components/Footer";

const DRAFT = "viralya-order-draft";

export default function Commande() {
  const navigate = useNavigate();
  const { data: config } = useQuery({ queryKey: ["config"], queryFn: fetchConfig });
  const [bump, setBump] = useState(false);
  const [activite, setActivite] = useState("ecommerce");
  const [niche, setNiche] = useState("");
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(null);
  const [idempotencyKey] = useState(() =>
    (window.crypto?.randomUUID ? window.crypto.randomUUID() : `idem-${Date.now()}-${Math.random().toString(16).slice(2)}`)
  );

  useEffect(() => {
    try {
      const d = JSON.parse(localStorage.getItem(DRAFT));
      if (!d || !d.email) { navigate("/pilote"); return; }
      setDraft(d);
    } catch { navigate("/pilote"); }
  }, [navigate]);

  const base = config?.prices?.pilote ?? 49;
  const biblePrice = config?.prices?.character_bible ?? 29;
  const total = base + (bump ? biblePrice : 0);

  const pay = async () => {
    if (!draft || paying) return;
    setPaying(true); setError(null);
    try {
      const { checkout_url } = await startCheckout({ bump, activite, niche, referrer: document.referrer || null, idempotency_key: idempotencyKey, ...draft });
      track("InitiateCheckout", { value: total, email: draft.email });
      window.location.href = checkout_url;
    } catch (e) {
      setError("Impossible d'ouvrir le paiement. Merci de réessayer.");
      setPaying(false);
    }
  };

  return (
    <div>
      <Seo routeKey="/pilote" path="/commande/pilote" title="Commande — Pilote 7 jours | VIRALYA" />
      <Header />
      <main className="page">
        <div className="container page-narrow">
          <Link to="/pilote" className="back-link"><ArrowLeft size={15} /> Étape précédente</Link>
          <div className="steps-bar"><div className="step active" /><div className="step active" /><div className="step" /></div>
          <span className="eyebrow"><span className="sq" />{fr.commande.eyebrow}</span>
          <h1 className="h2" style={{ marginBottom: "1.5rem" }}>{fr.commande.title}</h1>

          <div className="glass card" style={{ marginBottom: "1.25rem" }}>
            <div className="field full" style={{ marginBottom: "1rem" }}>
              <label>{fr.commande.activiteLabel}</label>
              <select value={activite} onChange={(e) => setActivite(e.target.value)} data-testid="commande-activite">
                {fr.commande.activiteOptions.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
            <div className="field full">
              <label>{fr.commande.nicheLabel}</label>
              <input value={niche} onChange={(e) => setNiche(e.target.value)} placeholder="Ex : montres de plongée pour collectionneurs" data-testid="commande-niche" />
            </div>
          </div>

          <div className="glass card" data-testid="commande-summary">
            <div className="summary-row"><span>{fr.commande.baseLabel}</span><span className="tnum">{chf(base)}</span></div>

            <label className="order-bump" data-testid="order-bump">
              <input type="checkbox" checked={bump} onChange={(e) => setBump(e.target.checked)} data-testid="order-bump-checkbox" />
              <div>
                <strong style={{ display: "flex", alignItems: "center", gap: 6 }}><Plus size={15} /> {fr.commande.bumpLabel} — {chf(biblePrice)}</strong>
                <span className="muted" style={{ fontSize: "0.88rem" }}>{fr.commande.bumpBody}</span>
              </div>
            </label>

            {bump && <div className="summary-row"><span>{fr.offer.bumpTitle}</span><span className="tnum">{chf(biblePrice)}</span></div>}
            <div className="summary-row total" style={{ marginTop: "0.5rem" }}><span>{fr.commande.total}</span><span className="tnum" data-testid="commande-total">{chf(total)}</span></div>

            <button className="btn btn--primary btn--block" style={{ marginTop: "1.6rem" }} onClick={pay} disabled={paying} data-testid="commande-pay">
              <Lock size={15} /> {paying ? "Traitement…" : `${fr.commande.payer} · ${chf(total)}`}
            </button>
            {error && <p className="form-status err" data-testid="commande-error">{error}</p>}
            <p className="muted" style={{ fontSize: "0.8rem", marginTop: "1rem", textAlign: "center" }}>{fr.commande.demoNote}</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", justifyContent: "center", marginTop: "1rem" }}>
              {fr.commande.trustMarks.map((t) => <span className="badge" key={t}>{t}</span>)}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
